import { confirmable } from 'react-confirm';
import StackedModal from './stacked_modal';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { RadioButton, TextField } from '@shopify/polaris';
import { Box, Stack, Typography } from '@mui/material';
import { utils, write, writeFile } from 'xlsx';
import { fDateTime } from 'src/utils/format_time';
import { requiredField } from 'src/utils/format_data';
import { writeEncryptedExcel } from 'src/utils/excel_encrypt';

function toNumberCell(cell) {
  if (cell.t !== 's') return;
  const text = String(cell.v).trim();
  if (!/^[1-9][0-9]*$/.test(text) || !Number.isSafeInteger(Number(text))) return;
  cell.t = 'n';
  cell.v = Number(text);
  delete cell.w;
  delete cell.h;
  delete cell.r;
}

function formatSheet(ws) {
  if (!ws['!ref']) return;
  const range = utils.decode_range(ws['!ref']);
  const widths = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    let width = 0;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cell = ws[utils.encode_cell({ r, c })];
      if (!cell) continue;
      if (r > range.s.r) toNumberCell(cell);
      if (cell.t === 'n' && Number.isInteger(cell.v)) cell.z = '0';
      width = Math.max(width, String(cell.w ?? cell.v ?? '').length);
    }
    widths.push({ wch: Math.min(Math.max(width + 2, 10), 40) });
  }
  ws['!cols'] = widths;
}

function ExportDialog({
  show,
  proceed,
  fileName,
  subtitle,
  selectedData = [],
  selectedCount,
  exportData,
  params,
  data,
  excelData,
  exportSelect = true,
  multipleItem = false,
  currentPage = true,
  manifest = false,
  allCount,
  withPassword = false,
}) {
  const { t } = useTranslation(['excel', 'placeholder']);
  const [option, setOption] = useState(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const canSubmit = option !== null && !(withPassword && !password.trim());
  const onSubmit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      await createExcel();
    } finally {
      setLoading(false);
    }
  };
  const createExcel = async () => {
    var res = [];
    if (option === 'all' && !manifest) {
      const allData = await exportData({
        ...params,
        pageNumber: 1,
        pageSize: allCount || 1000000,
      });
      res = allData;
    } else if (option === 'selected') {
      res = selectedData;
    } else if (option === 'current') {
      res = data;
    } else if (manifest) {
      res = data;
    }
    if (res.length > 0) {
      if (multipleItem && !manifest) {
        res = res.flatMap((x) => {
          const items = excelData(x);
          return Array.isArray(items) ? items : [items];
        });
      } else if (manifest) {
        res = res.map((x) => {
          return excelData(x);
        });
      } else {
        res = res.map((x) => excelData(x));
      }
      const ws = utils.json_to_sheet(res);
      formatSheet(ws);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, fileName);
      var stamp = fDateTime(new Date());
      if (withPassword) {
        const workbookArray = write(wb, { type: 'array', bookType: 'xlsx' });
        await writeEncryptedExcel(workbookArray, password.trim(), `${fileName}${stamp}.xlsx`);
      } else {
        writeFile(wb, `${fileName}${stamp}.xlsx`);
      }
      proceed(true);
    }
  };
  const content = (
    <Stack>
      <Typography
        variant={'body_md_medium'}
        gutterBottom
        sx={{ textTransform: 'capitalize' }}
      >
        {subtitle ?? fileName}
      </Typography>
      {currentPage && (
        <RadioButton
          label={t('excel:current')}
          checked={option === 'current'}
          onChange={() => setOption('current')}
          disabled={!data.length}
        />
      )}

      <RadioButton
        label={t('common:status.all')}
        checked={option === 'all'}
        onChange={() => setOption('all')}
        disabled={!data.length}
      />
      {exportSelect && (
        <RadioButton
          label={`${t('common:status.selected')}: ${selectedCount ?? selectedData.length}`}
          checked={option === 'selected'}
          onChange={() => setOption('selected')}
          disabled={!selectedData.length}
        />
      )}
      {withPassword && (
        <Box
          sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit();
            }
          }}
        >
          <Typography variant={'body_md_medium'}>{requiredField(t('excel:password'))}</Typography>
          <TextField
            type="password"
            autoComplete="new-password"
            name="excel-export-password"
            id="excel-export-password"
            label={t('excel:password')}
            labelHidden
            placeholder={t('placeholder:password')}
            value={password}
            onChange={setPassword}
          />
        </Box>
      )}
    </Stack>
  );

  return (
    <StackedModal
      isOpen={show}
      title={t('common:button.export')}
      content={content}
      primaryAction={{
        content: t('common:button.confirm'),
        onAction: onSubmit,
        loading: loading,
        disabled: !canSubmit,
      }}
      onClose={() => {
        if (loading) return;
        proceed(false);
      }}
    />
  );
}

export default confirmable(ExportDialog);
